import type { Equipment, LoadSource } from '@cadencia/contracts';
import { fromCentigrams, toCentigrams } from '../units/round.js';

export interface GeneratedLoad {
  loadKg: number;
  source: LoadSource;
}

export interface GenerateOptions {
  /** Necesario para equipamiento de peso corporal. */
  bodyWeightKg?: number;
  /** Tope de valores generados; evita explosiones combinatorias. */
  maxValues?: number;
}

const DEFAULT_MAX_VALUES = 2000;

/**
 * Enumera las cargas REALMENTE seleccionables de una instancia de equipamiento.
 * Requisito §8.1: una recomendacion nunca puede proponer una carga imposible.
 * Ver docs/LOAD_RECOMMENDATION_ENGINE.md §6.1.
 */
export function generateAvailableLoads(
  equipment: Equipment,
  options: GenerateOptions = {},
): GeneratedLoad[] {
  const maxValues = options.maxValues ?? DEFAULT_MAX_VALUES;

  switch (equipment.kind) {
    case 'machine_stack':
    case 'cable':
      return finish(generateStack(equipment), 'stack', maxValues);
    case 'dumbbell_set':
      return finish(generateDumbbells(equipment), 'dumbbell', maxValues);
    case 'barbell':
    case 'smith':
    case 'machine_plate_loaded':
      return finish(generatePlateLoaded(equipment, maxValues), 'computed_plates', maxValues);
    case 'bodyweight':
      return options.bodyWeightKg === undefined
        ? []
        : finish([toCentigrams(options.bodyWeightKg)], 'bodyweight', maxValues);
    case 'band':
    case 'other':
      return [];
    default: {
      const exhaustive: never = equipment.kind;
      throw new Error(`Tipo de equipamiento no soportado: ${String(exhaustive)}`);
    }
  }
}

function finish(centigrams: number[], source: LoadSource, maxValues: number): GeneratedLoad[] {
  const unique = [...new Set(centigrams)].filter((cg) => cg >= 0).sort((a, b) => a - b);
  const limited = unique.length > maxValues ? unique.slice(0, maxValues) : unique;
  return limited.map((cg) => ({ loadKg: fromCentigrams(cg), source }));
}

function generateStack(equipment: Equipment): number[] {
  const min = equipment.minLoadKg;
  const max = equipment.maxLoadKg;
  const step = equipment.incrementKg;
  if (min === null || max === null || step === null || step <= 0) return [];

  const values: number[] = [];
  const minCg = toCentigrams(min);
  const maxCg = toCentigrams(max);
  const stepCg = toCentigrams(step);
  const microCg =
    equipment.hasMicroIncrements && equipment.microIncrementKg !== null
      ? toCentigrams(equipment.microIncrementKg)
      : 0;

  for (let cg = minCg; cg <= maxCg; cg += stepCg) {
    values.push(cg);
    // Los add-on magneticos permiten un escalon intermedio sobre cada placa.
    if (microCg > 0 && cg + microCg <= maxCg) values.push(cg + microCg);
  }
  return values;
}

function generateDumbbells(equipment: Equipment): number[] {
  return equipment.dumbbellsKg.map(toCentigrams);
}

/**
 * Barra, multipower o maquina de discos: peso base + 2 x (suma de discos por lado).
 * Se enumeran las sumas alcanzables por lado con el inventario declarado.
 */
function generatePlateLoaded(equipment: Equipment, maxValues: number): number[] {
  const base = toCentigrams(equipment.barWeightKg ?? 0);
  if (equipment.plateInventory.length === 0) return [base];

  // Sumas alcanzables POR LADO. Cada par aporta un disco a cada lado.
  let perSide = new Set<number>([0]);
  for (const item of equipment.plateInventory) {
    if (item.pairs <= 0) continue;
    const plateCg = toCentigrams(item.plateKg);
    const next = new Set<number>(perSide);
    for (const current of perSide) {
      for (let count = 1; count <= item.pairs; count += 1) {
        next.add(current + plateCg * count);
        if (next.size > maxValues * 4) break;
      }
    }
    perSide = next;
  }

  const maxCg = equipment.maxLoadKg === null ? Number.POSITIVE_INFINITY : toCentigrams(equipment.maxLoadKg);
  const totals: number[] = [];
  for (const side of perSide) {
    const total = base + side * 2;
    if (total <= maxCg) totals.push(total);
  }
  return totals;
}
